import React from "react";

export enum ViewingStatus {
  AWAITING_CONFIRMATION = "awaiting_confirmation",
  SCHEDULED = "scheduled",
  COMPLETED = "completed",
  NO_SHOW = "no_show",
  CANCELLED = "cancelled",
}

export type ViewingType = "physical" | "virtual";

export type AttendeeResponseStatus =
  | "accepted"
  | "declined"
  | "tentative"
  | "maybe";

export type ViewMode = "list" | "grid";

export type FilterChipId =
  | "all"
  | "on-site"
  | "virtual"
  | "today"
  | "this-week"
  | "unassigned";

export interface ViewingAssignee {
  user_id: string;
  user_name: string;
  user_role?: string;
  user_email?: string;
  profile_picture?: string | null;
}

export interface ViewingChosenProperty {
  property_id: number;
  property_name: string;
}

export interface ViewingPropertyDetails {
  chosen_properties: ViewingChosenProperty[];
  room_type: string | null;
}

export interface ViewingVirtualMeetDetails {
  meeting_platform: string;
  joining_link: string;
}

export interface ViewingPhysicalMeetDetails {
  location: string;
}

export interface ViewingLeadSummary {
  lead_id: string;
  name: string;
  reference_id: string;
}

export interface ViewingQA {
  question_id: number;
  question: string;
  answer: string[];
}

export interface ViewingsData {
  _id: string;
  name: string;
  event_created_at?: string;
  event_name: string;
  viewing_type: ViewingType;
  status: ViewingStatus;
  scheduled_time: string;
  property_details: ViewingPropertyDetails | null;
  virtual_meet_details: ViewingVirtualMeetDetails | null;
  physical_meet_details: ViewingPhysicalMeetDetails | null;
  assignees: ViewingAssignee[];
  attendee_response_status: AttendeeResponseStatus | null;
  event_guests?: string[];
  crm_questions_and_answers?: ViewingQA[];
  lead_id?: string;
  lead?: ViewingLeadSummary;
  created_by?: ViewingAssignee;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface FormattedScheduleDateTime {
  date: string;
  time: string;
}

export interface ViewingsBoardStage {
  key: string;
  title: string;
  primary_hex_code: string;
  secondary_hex_code: string;
  order: number;
  viewings: ViewingsData[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface GetViewingsBoardResponse {
  stages: ViewingsBoardStage[];
}

export type ViewingsSortKey = "scheduled_at" | "created_at" | "updated_at";

export type ViewingsSortOrder = "asc" | "desc";

export interface ListViewingsParams {
  page?: number;
  limit?: number;
  search?: string;
  sort_key?: ViewingsSortKey;
  sort_order?: ViewingsSortOrder;
  scheduled_at_from?: string;
  scheduled_at_to?: string;
  created_at_from?: string;
  created_at_to?: string;
  property_ids?: string[];
  has_no_property?: boolean;
  attendee_response_status?: string[];
}

export interface GetViewingsListParams {
  status: string;
  page: number;
  limit: number;
  search?: string;
  sort_key?: ViewingsSortKey;
  sort_order?: ViewingsSortOrder;
  attendee_response_status?: AttendeeResponseStatus[];
  property_ids?: string[];
  has_no_property?: boolean;
  scheduled_at_from?: string;
  scheduled_at_to?: string;
  created_at_from?: string;
  created_at_to?: string;
}

export interface GetViewingsListResponse {
  viewings: ViewingsData[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface StatusSectionPillProps {
  status: ViewingStatus;
  count: number;
  variant?: "filled" | "outlined";
  className?: string;
  showChevron?: boolean;
}

export interface AttendeeResponseBadgeProps {
  response: AttendeeResponseStatus | null | undefined;
  className?: string;
}

export interface ViewingsHeaderProps {
  pluralLabel: string;
  addActionLabel: string;
  totalCount: number;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onExportClick?: () => void;
}

export interface ViewingsTableSectionProps {
  stage: ViewingsBoardStage;
  isSortPopupOpen: boolean;
  onSortPopupToggle: (stageKey: string) => void;
}

export interface ViewingsListViewProps {
  stages: ViewingsBoardStage[];
}

export interface ViewingsKanbanViewProps {
  stages: ViewingsBoardStage[];
}

export interface ViewingsKanbanColumnProps {
  stage: ViewingsBoardStage;
  isSortPopupOpen: boolean;
  onSortPopupToggle: (stageKey: string) => void;
}

export interface ViewingsKanbanCardProps {
  viewing: ViewingsData;
}

export interface ViewingsStageSortPopupProps {
  currentOrder: ViewingsSortOrder | null;
  onSortChange: (order: ViewingsSortOrder | null) => void;
  onClose: () => void;
}

export interface ViewingsEmptyStateProps {
  pluralLabel: string;
  singularLabel: string;
  addActionLabel: string;
}

export interface ViewingsListShimmerProps {
  rowCount?: number;
  stageCount?: number;
}

export interface ViewingsKanbanShimmerProps {
  columnCount?: number;
  cardsPerColumn?: number[];
}

export interface TruncatedCommaListWithMoreProps {
  items: string[];
  separator?: string;
  pillReservePx?: number;
  textClassName?: string;
  pillClassName?: string;
  containerClassName?: string;
  side?: "top" | "right" | "bottom" | "left";
  renderMoreLabel?: (hiddenCount: number) => React.ReactNode;
  tooltipContent?: React.ReactNode;
  tooltipContentClassName?: string;
  tooltipArrowClassName?: string;
  tooltipMaxWidth?: string | number;
}

export interface AssigneesHoverPopupProps {
  count?: number;
  assignees: ViewingAssignee[];
  triggerClassName?: string;
  triggerLabel?: React.ReactNode;
  closeDelayMs?: number;
  popupGapPx?: number;
  placement?: "top" | "bottom";
}

export interface UserResponseFilterOption {
  id: string;
  badgeValue: AttendeeResponseStatus;
  values: AttendeeResponseStatus[];
}

export interface ViewingsUserResponseFilterProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  selected: AttendeeResponseStatus[];
  onApply: (next: AttendeeResponseStatus[]) => void;
  onClose: () => void;
}

export interface PropertyOption {
  id: string;
  name: string;
}

export interface ViewingsPreferredPropertyFilterProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  selected: string[];
  onChange: (next: string[]) => void;
  onClose: () => void;
}

export interface ViewingsTableColumnConfig {
  id: string;
  header: string;
  width: number;
  sortable?: boolean;
}

export interface ViewingsColumnFilterArgs {
  selectedPropertyIds: string[];
  onPreferredPropertyChange: (next: string[]) => void;
  rowCount: number;
  onSuppressFloatingActions?: (suppress: boolean) => void;
}

export interface HeaderCellWithFilterProps {
  config: ViewingsTableColumnConfig;
  showFilter: boolean;
  renderFilter: (args: {
    open: boolean;
    anchorRef: React.RefObject<HTMLButtonElement | null>;
    onClose: () => void;
  }) => React.ReactNode;
}

export interface ViewingsRowActionMenuProps {
  viewing: ViewingsData;
  triggerRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  hideMarkComplete?: boolean;
  hideViewDetails?: boolean;
  hideJoinMeeting?: boolean;
  onDeleteClick?: () => void;
}

export interface ViewingsFloatingActionsProps {
  rowRef: React.RefObject<HTMLTableRowElement> | null;
  viewing?: ViewingsData;
  isVisible: boolean;
  onHover?: (hovered: boolean) => void;
  onPopupStateChange?: (open: boolean) => void;
}
